import { createSlice } from '@reduxjs/toolkit';

import type { RootState } from 'app/store';

import { SocketResponse, SocketSuccessResponse } from 'types/jobDescription';

import {
  disconnectJobDescription,
  generateDescription,
  initializeJobDescriptionSocket,
  receiveGeneratedDescription
} from './jobDescriptionThunks';

interface JobDescriptionState {
  isConnected: boolean;
  isGenerating: boolean;
  error: string | null;
  jobId: string | null;
  description: string | null;
  qualifications: string | null;
  skillRequirement: string[] | null;
  languages: string[] | null;
}

export const initialState: JobDescriptionState = {
  isConnected: false,
  isGenerating: false,
  error: null,
  jobId: null,
  description: null,
  qualifications: null,
  skillRequirement: null,
  languages: null
};

const jobDescriptionSlice = createSlice({
  name: 'jobDescription',
  initialState,
  reducers: {
    resetGeneration: (state) => {
      state.isGenerating = false;
      state.error = null;
      state.description = null;
      state.qualifications = null;
      state.skillRequirement = null;
      state.languages = null;
    },
    dropState: () => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      // Initialize Socket
      .addCase(initializeJobDescriptionSocket.pending, (state) => {
        state.isConnected = false;
        state.error = null;
      })
      .addCase(initializeJobDescriptionSocket.fulfilled, (state, action) => {
        const payload = action.payload as SocketResponse;
        if (payload.success) {
          state.isConnected = true;
          state.jobId = payload.jobId;
        } else {
          state.error = payload.error;
        }
      })
      .addCase(initializeJobDescriptionSocket.rejected, (state, action) => {
        state.isConnected = false;
        state.error = action.error.message || 'Failed to connect';
      })
      // Generate Description
      .addCase(generateDescription.pending, (state) => {
        state.isGenerating = true;
        state.error = null;
      })
      .addCase(generateDescription.fulfilled, (state, action) => {
        const payload = action.payload as SocketResponse;
        if (!payload.success) {
          state.error = payload.error;
          state.isGenerating = false;
        }
      })
      .addCase(generateDescription.rejected, (state, action) => {
        state.isGenerating = false;
        state.error = action.error.message || 'Failed to generate description';
      })
      // Receive Generated Description
      .addCase(receiveGeneratedDescription.fulfilled, (state, action) => {
        state.isGenerating = false;
        state.description = action.payload.description;
        state.qualifications = action.payload.qualifications;
        state.skillRequirement = action.payload.skillRequirement;
        state.languages = action.payload.languages;
      })
      // Disconnect
      .addCase(disconnectJobDescription.fulfilled, (state) => {
        state.isConnected = false;
        state.jobId = null;
      });
  }
});

export const { resetGeneration, dropState } = jobDescriptionSlice.actions;

export default jobDescriptionSlice.reducer;
